import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import { ReactElement, cloneElement, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";

import { COLORS, STYLES } from "@constants";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { track } from "@utils/appUtils";

interface Props {
  children: ReactElement | ReactElement[];
  tags: ReactElement[];
}

const TooltipWrapper = ({ children, tags }: Props) => {
  const [open, setOpen] = useState(false);
  const tooltipId = useStableId();

  const displayedTags = useMemo(
    () =>
      tags.map((tag) =>
        cloneElement(tag, {
          theme: "neutral",
          key: tag.key ?? `${tooltipId}-${Math.random().toString(36).slice(2)}`,
        })
      ),
    [tags, tooltipId]
  );

  const { x, y, refs, strategy } = useFloating({
    open,
    placement: "top",
    strategy: "absolute",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  let hoverTimeout: ReturnType<typeof setTimeout>;
  const toggle = (state: boolean) => {
    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => setOpen(state), 100);
  };

  useEffect(() => {
    if (open) {
      track(SegmentEventName.VIEW, { ui_component: "TagTooltip" });
    }
  }, [open]);

  const size = tags[0]?.props?.size;

  return (
    <Wrapper
      ref={refs.setReference}
      onMouseEnter={() => toggle(true)}
      onMouseLeave={() => toggle(false)}
      aria-describedby={tooltipId}
    >
      {children}

      {open &&
        createPortal(
          <Container
            id={tooltipId}
            ref={refs.setFloating}
            $size={size}
            $columns={Math.ceil(displayedTags.length / 10)}
            style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
          >
            {displayedTags.map((tag) => (
              <TagContainer key={tag.key} $size={size}>
                {tag}
              </TagContainer>
            ))}
          </Container>,
          document.body
        )}
    </Wrapper>
  );
};

const useStableId = () => {
  const idRef = useRef<string>();
  if (!idRef.current) {
    idRef.current = `tt-${Math.random().toString(36).slice(2, 10)}`;
  }
  return idRef.current;
};

export default TooltipWrapper;

const Wrapper = styled.div`
  position: relative;
  width: fit-content;
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

interface ContainerProps {
  $size?: string;
  $columns: number;
}

const Container = styled.div<ContainerProps>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, auto);
  gap: 0 20px;

  padding: ${({ $size }) => ($size === "xs" ? "5px 11px 9px" : "10px 22px 18px")};
  border-radius: 8px;
  box-shadow: ${STYLES.SHADOW_B};
  background-color: ${COLORS.WHITE};
  z-index: 510;

  animation: ${fadeIn} 80ms ease-out;
`;

const TagContainer = styled.div<{ $size?: string }>`
  margin-top: ${({ $size }) => ($size === "xs" ? "0" : "8px")};
`;
